import { KnownApiError } from "@mirage-web/networking/known-api-error";
// @oasis-permission-inherits: /cloudflare-redirects (redirects:manage)
import { createFileRoute } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { toast } from "sonner";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { getApiError } from "~/lib/network/errors";
import { getRedirectValidationErrors } from "~/modules/cloudflare-redirects/errors";
import {
  applyRedirectsMutation,
  downloadRedirectsMutation,
  getRedirectsStatusMutation,
  previewRedirectsMutation,
} from "~/modules/cloudflare-redirects/mutations";
import type {
  RedirectAddition,
  RedirectDiff,
  RedirectUpdate,
  RedirectValidationError,
} from "~/modules/cloudflare-redirects/schemas";
import { toastPersistentError } from "~/modules/cloudflare-redirects/toasts";

export const Route = createFileRoute("/_dash/cloudflare-redirects/")({
  component: RouteComponent,
});

function RouteComponent() {
  const [csvText, setCsvText] = useState("");
  const [previewResult, setPreviewResult] = useState<RedirectDiff | null>(null);
  const [validationErrors, setValidationErrors] = useState<RedirectValidationError[] | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const hasPendingChanges =
    !!previewResult && (previewResult.toAdd.length > 0 || previewResult.toUpdate.length > 0);

  const { mutateAsync: downloadCsv } = downloadRedirectsMutation.useMutation();
  const { mutateAsync: previewChanges } = previewRedirectsMutation.useMutation();
  const { mutateAsync: applyChanges } = applyRedirectsMutation.useMutation();
  const { mutateAsync: pollStatus } = getRedirectsStatusMutation.useMutation();

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    try {
      const { csv } = await downloadCsv({ params: {} });
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "cloudflare_redirects.csv";
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to download redirects";
      toastPersistentError(message);
    } finally {
      setIsDownloading(false);
    }
  }, [downloadCsv]);

  const handlePreview = useCallback(async () => {
    if (!csvText.trim()) {
      return;
    }
    setIsPreviewLoading(true);
    setPreviewResult(null);
    setValidationErrors(null);
    try {
      const diff = await previewChanges({ params: { csvContent: csvText } });
      setPreviewResult(diff);
    } catch (error) {
      if (error instanceof Error) {
        const previewValidationErrors = getRedirectValidationErrors(error);
        if (previewValidationErrors) {
          setValidationErrors(previewValidationErrors);
          return;
        }
      }
      const message = error instanceof Error ? error.message : "Failed to preview changes";
      toastPersistentError(message);
    } finally {
      setIsPreviewLoading(false);
    }
  }, [csvText, previewChanges]);

  const handleApplyConfirm = useCallback(async () => {
    if (!previewResult) {
      return;
    }

    const toAddCount = previewResult.toAdd.length;
    const toUpdateCount = previewResult.toUpdate.length;

    setConfirmOpen(false);
    setIsApplying(true);

    let settled = false;
    let pollingActive = true;
    let applyResolved = false;

    const finish = (outcome: "success" | "error", message?: string) => {
      if (settled) {
        return;
      }
      settled = true;
      pollingActive = false;
      setIsApplying(false);
      if (outcome === "success") {
        toast.success(`Changes applied — ${toAddCount} added, ${toUpdateCount} updated.`);
        setCsvText("");
        setPreviewResult(null);
        setValidationErrors(null);
      } else {
        toastPersistentError(message ?? "Failed to apply changes");
      }
    };

    (async () => {
      const maxAttempts = 60;
      for (let i = 0; i < maxAttempts; i++) {
        if (!pollingActive) {
          return;
        }
        await new Promise<void>((resolve) => setTimeout(resolve, 2000));
        if (!pollingActive) {
          return;
        }
        try {
          const status = await pollStatus({ params: {} });
          if (applyResolved) {
            if (status.status === "completed") {
              finish("success");
              return;
            }
            if (status.status === "failed") {
              finish("error", status.error);
              return;
            }
          }
        } catch {
          // Ignore individual poll failures and keep retrying
        }
      }
      if (!settled) {
        finish("error", "Operation timed out");
      }
    })();

    try {
      await applyChanges({
        params: { csvContent: csvText, expectedChecksum: previewResult.checksum },
      });
      applyResolved = true;
    } catch (error) {
      if (error instanceof KnownApiError) {
        const apiError = getApiError(error);
        if (error.responseStatus === 409) {
          if (settled) {
            return;
          }
          settled = true;
          pollingActive = false;
          setIsApplying(false);
          toastPersistentError(
            apiError?.message ?? error.message ?? "Conflict: please re-preview changes"
          );
          setPreviewResult(null);
        } else {
          applyResolved = true;
          finish("error", apiError?.message ?? error.message ?? "Failed to apply changes");
        }
      } else {
        // Non-KnownApiError (network drop, connection timeout): polling loop continues as fallback
        applyResolved = true;
      }
    }
  }, [csvText, previewResult, applyChanges, pollStatus]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Current redirects</CardTitle>
          <CardDescription>Download the active redirect list as a CSV file.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleDownload} disabled={isDownloading}>
            {isDownloading ? "Downloading..." : "Download current redirects (CSV)"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Update redirects</CardTitle>
          <CardDescription>Paste a new redirect list to preview and apply changes.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="csv-input">Paste new redirects (CSV)</Label>
            <textarea
              id="csv-input"
              className="min-h-32 w-full rounded-md border bg-background px-3 py-2 font-mono text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="captions.ai/old-path,https://captions.ai/new-path"
              value={csvText}
              onChange={(e) => {
                setCsvText(e.target.value);
                setPreviewResult(null);
                setValidationErrors(null);
              }}
              disabled={isApplying}
            />
          </div>

          <Button
            onClick={handlePreview}
            disabled={!csvText.trim() || isPreviewLoading || isApplying}
            variant="outline"
          >
            {isPreviewLoading ? "Previewing..." : "Preview changes"}
          </Button>

          {validationErrors && (
            <div
              role="alert"
              className="relative space-y-2 rounded-md border border-destructive bg-destructive/10 p-4 pr-10 text-sm text-destructive"
            >
              <button
                type="button"
                aria-label="Dismiss validation errors"
                className="absolute top-3 right-3 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                onClick={() => setValidationErrors(null)}
              >
                <XIcon className="size-4" />
              </button>
              <p className="font-medium">
                {validationErrors.length === 1
                  ? "1 validation error"
                  : `${validationErrors.length} validation errors`}
              </p>
              <ul className="list-disc space-y-1 pl-5 font-mono text-xs">
                {validationErrors.map((validationError, index) => (
                  <li key={index}>
                    {validationError.row !== null
                      ? `Row ${validationError.row}: ${validationError.message}`
                      : validationError.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {previewResult && (
            <div className="space-y-4">
              <Tabs defaultValue="to-add">
                <TabsList>
                  <TabsTrigger value="to-add">
                    Adding
                    <Badge variant="secondary" className="ml-2">
                      {previewResult.toAdd.length}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger value="to-update">
                    Updating
                    <Badge variant="secondary" className="ml-2">
                      {previewResult.toUpdate.length}
                    </Badge>
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="to-add">
                  <RedirectAdditionTable entries={previewResult.toAdd} />
                </TabsContent>
                <TabsContent value="to-update">
                  <RedirectUpdateTable entries={previewResult.toUpdate} />
                </TabsContent>
              </Tabs>

              <Button
                onClick={() => setConfirmOpen(true)}
                disabled={isApplying || !hasPendingChanges}
              >
                {isApplying ? "Applying changes to Cloudflare..." : "Apply changes"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply redirect changes?</DialogTitle>
            <DialogDescription>
              {previewResult
                ? `This will add ${previewResult.toAdd.length} redirect(s) and update ${previewResult.toUpdate.length} redirect(s).`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleApplyConfirm} disabled={isApplying || !hasPendingChanges}>
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

type RedirectTableColumn<T> = {
  header: string;
  render: (entry: T) => React.ReactNode;
};

function RedirectTableShell<T extends { sourceUrl: string }>({
  entries,
  columns,
}: {
  entries: T[];
  columns: RedirectTableColumn<T>[];
}) {
  if (entries.length === 0) {
    return <p className="py-4 text-center text-sm text-muted-foreground">No changes</p>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((column) => (
            <TableHead key={column.header}>{column.header}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.map((entry, index) => (
          <TableRow key={`${entry.sourceUrl}-${index}`}>
            {columns.map((column) => (
              <TableCell key={column.header} className="font-mono text-xs">
                {column.render(entry)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function RedirectAdditionTable({ entries }: { entries: RedirectAddition[] }) {
  return (
    <RedirectTableShell
      entries={entries}
      columns={[
        { header: "source_url", render: (entry) => entry.sourceUrl },
        { header: "target_url", render: (entry) => entry.targetUrl },
      ]}
    />
  );
}

function RedirectUpdateTable({ entries }: { entries: RedirectUpdate[] }) {
  return (
    <RedirectTableShell
      entries={entries}
      columns={[
        { header: "source_url", render: (entry) => entry.sourceUrl },
        { header: "previous_target_url", render: (entry) => entry.previousTargetUrl },
        { header: "target_url", render: (entry) => entry.targetUrl },
      ]}
    />
  );
}
